import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, AlertCircle, CheckCircle2, Bug, Lightbulb } from "lucide-react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { getAdminFeedbacksCentralAjuda } from "../../api";
import SideBarNavCentralAjuda from "../../componets/SideBarNavCentralAjuda";

const STATUS_OPCOES = [
  { value: "aberto", label: "Aberto", cor: "#F59E0B", icone: Clock },
  { value: "em_analise", label: "Em análise", cor: "#3B82F6", icone: AlertCircle },
  { value: "resolvido", label: "Resolvido", cor: "#10B981", icone: CheckCircle2 },
];

function KpiCard({ icon: Icon, label, value, cor }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className="p-3 rounded-xl" style={{ background: `${cor}1A` }}>
        <Icon className="w-5 h-5" style={{ color: cor }} />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

export default function CentralAjudaDashboard() {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErro(null);
      try {
        setFeedbacks((await getAdminFeedbacksCentralAjuda()) || []);
      } catch (e) {
        setErro(e.message || "Não foi possível carregar os envios.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const porStatus = useMemo(() => {
    const contagem = { aberto: 0, em_analise: 0, resolvido: 0 };
    feedbacks.forEach((f) => { contagem[f.status] = (contagem[f.status] || 0) + 1; });
    return STATUS_OPCOES.map((s) => ({ status: s.value, label: s.label, qtd: contagem[s.value] || 0 }));
  }, [feedbacks]);

  const porTipoStatus = useMemo(() => {
    const linhas = { bug: { tipo: "Bugs", aberto: 0, em_analise: 0, resolvido: 0 }, sugestao: { tipo: "Sugestões", aberto: 0, em_analise: 0, resolvido: 0 } };
    feedbacks.forEach((f) => {
      const linha = linhas[f.tipo];
      if (linha) linha[f.status] = (linha[f.status] || 0) + 1;
    });
    return Object.values(linhas);
  }, [feedbacks]);

  const total = feedbacks.length;
  const totalBugs = feedbacks.filter((f) => f.tipo === "bug").length;
  const totalSugestoes = feedbacks.filter((f) => f.tipo === "sugestao").length;

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <SideBarNavCentralAjuda isLight />

      <div className="flex flex-col flex-1 bg-gray-50" style={{ overflow: "auto" }}>
        <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="p-1.5 rounded-lg hover:bg-white/10 transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-bold tracking-tight">Central de Ajuda · Dashboard de Sugestões e Bugs</span>
          </div>
          <button
            onClick={() => navigate("/inicio")}
            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
          >
            Menu Principal
          </button>
        </header>

        <main className="flex-1 px-6 py-8 max-w-6xl mx-auto w-full">
          {loading && <div className="py-16 text-center text-gray-400 text-sm">Carregando dados…</div>}
          {!loading && erro && <div className="py-16 text-center text-red-500 text-sm">{erro}</div>}

          {!loading && !erro && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                {STATUS_OPCOES.map((s) => (
                  <KpiCard
                    key={s.value}
                    icon={s.icone}
                    label={s.label}
                    value={porStatus.find((p) => p.status === s.value)?.qtd ?? 0}
                    cor={s.cor}
                  />
                ))}
                <KpiCard icon={Bug} label="Bugs" value={totalBugs} cor="#EF4444" />
                <KpiCard icon={Lightbulb} label="Sugestões" value={totalSugestoes} cor="#A855F7" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Status dos envios</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    {total} envio{total === 1 ? "" : "s"} no total, por status.
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={porStatus} dataKey="qtd" nameKey="label" innerRadius={55} outerRadius={85} paddingAngle={2}>
                        {porStatus.map((s, idx) => (
                          <Cell key={s.status} fill={STATUS_OPCOES[idx].cor} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 justify-center mt-2">
                    {porStatus.map((s, idx) => (
                      <div key={s.status} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: STATUS_OPCOES[idx].cor }} />
                        {s.label} ({s.qtd})
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-3">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Bugs x Sugestões, por status</h3>
                  <p className="text-xs text-gray-500 mb-4">Quantos de cada tipo estão em aberto, em análise ou resolvidos.</p>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={porTipoStatus} margin={{ left: 4, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                      <XAxis dataKey="tipo" tick={{ fontSize: 12, fill: "#475569" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => STATUS_OPCOES.find((s) => s.value === v)?.label || v} />
                      <Bar dataKey="aberto" stackId="status" fill={STATUS_OPCOES[0].cor} />
                      <Bar dataKey="em_analise" stackId="status" fill={STATUS_OPCOES[1].cor} />
                      <Bar dataKey="resolvido" stackId="status" fill={STATUS_OPCOES[2].cor} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </main>

        <footer className="text-center text-xs text-gray-400 py-6">
          © {new Date().getFullYear()} Youx Group · Central de Ajuda
        </footer>
      </div>
    </div>
  );
}
